import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { API } from "../App";
import { ArrowLeft, Loader2, RotateCcw } from "lucide-react";
import FuturaIcon from "../components/FuturaIcon";
import { ResponsiveContainer, RadialBarChart, RadialBar, PieChart, Pie, Cell } from "recharts";
import { ProbabilitySection, RisksBenefitsSection, TimelineSection, ImpactCards } from "../components/SimulationSections";
import axios from "axios";

const RADIAL_BG = { fill: "#E5E5EA" };

const SimulationResult = () => {
  const navigate = useNavigate();
  const { decisionId } = useParams();
  const [decision, setDecision] = useState(null);
  const [simulation, setSimulation] = useState(null);
  const [loading, setLoading] = useState(true);

  const confidenceData = useMemo(
    () => [{ value: simulation?.confidence_level ?? 0 }],
    [simulation?.confidence_level]
  );

  const successData = useMemo(() => [
    { name: "Exito", value: simulation?.success_probability ?? 0, fill: "#10B981" },
    { name: "Fracaso", value: simulation?.failure_probability ?? 0, fill: "#EF4444" }
  ], [simulation?.success_probability, simulation?.failure_probability]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [decisionRes, simRes] = await Promise.all([
          axios.get(`${API}/decisions/${decisionId}`, { withCredentials: true }),
          axios.get(`${API}/decisions/${decisionId}/simulation`, { withCredentials: true })
        ]);
        setDecision(decisionRes.data);
        setSimulation(simRes.data);
      } catch (error) {
        navigate("/dashboard");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [decisionId, navigate]); // eslint-disable-line react-hooks/exhaustive-deps -- fetches once when id changes

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F7] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-[#002FA7] border-t-transparent rounded-full animate-spin" />
          <p className="text-[#6B7280]">Cargando resultados...</p>
        </div>
      </div>
    );
  }

  if (!simulation) {
    return (
      <div className="min-h-screen bg-[#F5F5F7] flex items-center justify-center">
        <div className="text-center">
          <p className="text-[#6B7280] mb-4">No se encontro la simulacion</p>
          <Button onClick={() => navigate("/dashboard")}>Volver al inicio</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F7]">
      <header className="bg-white border-b border-[#E5E5EA] sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-[#6B7280] hover:text-[#111111] transition-colors">
            <ArrowLeft className="w-5 h-5" /><span className="font-medium">Dashboard</span>
          </Link>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#002FA7] to-[#5B21B6] flex items-center justify-center p-1"><FuturaIcon className="w-full h-full text-white" /></div>
            <span className="font-['Outfit'] font-semibold text-lg text-[#111111]">FUTURA</span>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        <div className="mb-8 animate-fade-in">
          <h1 className="font-['Outfit'] text-3xl font-semibold text-[#111111] tracking-tight mb-2">Resultados de la simulacion</h1>
          <p className="text-[#6B7280]">{decision?.title}</p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <ProbabilitySection simulation={simulation} successData={successData} PieChart={PieChart} Pie={Pie} Cell={Cell} ResponsiveContainer={ResponsiveContainer} />
            <RisksBenefitsSection simulation={simulation} />
            <TimelineSection simulation={simulation} />
            <ImpactCards simulation={simulation} />
          </div>

          <div className="space-y-6">
            <div className="bg-white rounded-3xl p-6 animate-fade-in stagger-2" data-testid="confidence-card">
              <h3 className="font-['Outfit'] text-lg font-semibold text-[#111111] mb-4">Nivel de confianza</h3>
              <div className="relative flex justify-center">
                <div className="w-32 h-32">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadialBarChart cx="50%" cy="50%" innerRadius="70%" outerRadius="100%" startAngle={180} endAngle={0} data={confidenceData}>
                      <RadialBar background={RADIAL_BG} dataKey="value" cornerRadius={10} fill="#002FA7" />
                    </RadialBarChart>
                  </ResponsiveContainer>
                </div>
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 text-center">
                  <p className="text-3xl font-['Outfit'] font-bold text-[#111111]">{simulation.confidence_level}%</p>
                </div>
              </div>
              <p className="text-center text-sm text-[#6B7280] mt-2">Basado en la información proporcionada</p>
            </div>

            <div className="bg-gradient-to-br from-[#002FA7] to-[#5B21B6] rounded-3xl p-6 animate-fade-in stagger-3" data-testid="recommendation-card">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center"><Target className="w-5 h-5 text-white" /></div>
                <h3 className="font-['Outfit'] text-lg font-semibold text-white">Recomendacion</h3>
              </div>
              <p className="text-white/90 leading-relaxed">{simulation.recommendation}</p>
            </div>

            <div className="bg-white rounded-3xl p-6 animate-fade-in stagger-4" data-testid="scenarios-card">
              <h3 className="font-['Outfit'] text-lg font-semibold text-[#111111] mb-4">Escenarios alternativos</h3>
              <div className="space-y-3">
                {simulation.alternative_scenarios?.map((scenario) => (
                  <div key={scenario.name} className="p-3 bg-[#F5F5F7] rounded-xl">
                    <div className="flex items-center justify-between mb-1"><span className="font-medium text-[#111111] text-sm">{scenario.name}</span><span className="text-xs text-[#002FA7] font-medium">{scenario.probability}%</span></div>
                    <p className="text-xs text-[#6B7280]">{scenario.description}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-3 animate-fade-in stagger-5">
              <Button onClick={() => navigate("/new-decision")} className="w-full bg-[#002FA7] hover:bg-[#001d6a] text-white font-medium rounded-xl py-6" data-testid="new-simulation-btn">
                <RotateCcw className="w-5 h-5 mr-2" />Nueva simulacion
              </Button>
              <Button onClick={() => navigate("/dashboard")} variant="outline" className="w-full border-[#E5E5EA] text-[#111111] font-medium rounded-xl py-6" data-testid="back-dashboard-btn">
                Volver al dashboard
              </Button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SimulationResult;
